<script lang="ts">
	import type { ButtonColor, ButtonVariant } from '$lib/components/atoms/button/types';
	import type { IconPosition } from '$lib/components/atoms/iconlabel/types';
	import type { Target } from '$lib/components/atoms/link/types';
	import type { IconName } from '$lib/components/icons/types';
	import type { Snippet } from 'svelte';
	import type { ClassValue } from 'svelte/elements';
	import Button from '$lib/components/atoms/button/Button.svelte';
	import Link from '$lib/components/atoms/link/Link.svelte';
	import HeaderLogo from './HeaderLogo.svelte';

	interface HeaderCTA {
		hideTextOnMobile?: boolean;
		icon?: IconName;
		ariaLabel?: string;
		iconPosition?: IconPosition;
		label?: string;
		target?: Target;
		href?: string;
		text?: string;
		variant?: ButtonVariant;
		color?: ButtonColor;
		class?: string;
	}

	type HeaderVariant = 'default' | 'map-view' | 'no-logo';

	interface Props {
		variant?: HeaderVariant;
		rightButton?: HeaderCTA;
		leftButton?: HeaderCTA;
		hasRoleBanner?: boolean;
		class?: ClassValue;
		left?: Snippet;
		right?: Snippet;
		onLogoClick?: () => void;
		onRightButtonClick?: () => void;
		onLeftButtonClick?: () => void;
	}

	const {
		variant = 'default',
		rightButton,
		leftButton,
		hasRoleBanner = false,
		class: classes,
		left,
		right,
		onLogoClick,
		onRightButtonClick,
		onLeftButtonClick,
	}: Props = $props();

	/**
	 * Header variants page inventory
	 * default: home, iti/search, iti/edit, route/explo, route/corridor, route/roadsheet, route/cost, route/expense, accounts, error
	 * no-logo: traffic, datasheet, zoi, route/expense/edit, route/expense/print
	 * map-view: map full screen
	 */

	const mapclassesToVariant: Record<HeaderVariant, string> = {
		'map-view': 'bg-neutral-weakest md:rd-2 md:p-2 header-backdrop',
		'default': 'md:py-0',
		'no-logo': 'px-2 md:py-0',
	};

	const mobileColor: ButtonColor | undefined = $derived(variant !== 'map-view' ? undefined : 'light');
</script>

<header
	role={hasRoleBanner ? 'banner' : null}
	class={['flex items-center w-full py-1 justify-between', classes, mapclassesToVariant[variant]]}

>
	{#if left}{@render left()}{:else}
		{#if variant !== 'no-logo'}
			<HeaderLogo {onLogoClick} />
		{:else if leftButton}
			{#if leftButton.href}
				<Link
					button="filled"
					color="white"
					href={leftButton.href}
					{...leftButton}
					onclick={onLeftButtonClick}
				/>
			{:else}
				<Button
					color="white"
					{mobileColor}
					{...leftButton}
					onclick={onLeftButtonClick}
				/>
			{/if}
		{/if}
	{/if}

	{#if right}{@render right()}{:else}
		{#if rightButton}
			{#if rightButton?.href}
				<Link button="filled" color="white" href={rightButton.href} {...rightButton} />
			{:else}
				<Button
					color="white"
					{mobileColor}
					{...rightButton}
					onclick={onRightButtonClick}
				/>
			{/if}
		{/if}
	{/if}
</header>
